import { fulfilled } from 'lib/promiseMiddlewareHelpers'
import { Set, Map, Record } from 'immutable'
import { userActions, userSelectors } from 'data/resources/users'
import { getCurrentUser } from 'lib/utils'
import _ from 'lodash'

const selectors = {
  userFriendState: (state, userId) => {
    return state.friends.get(userId, FriendsState())
  },
  userFriendIds: (state, userId) => (
    selectors.userFriendState(state, userId).friendIds
  ),
  userFriends: (state, userId) => {
    return selectors.userFriendIds(state, userId).map(fid => {
      return userSelectors.find(state, fid)
    })
  },
  userPendingIncomingFriendIds: (state, userId) => (
    selectors.userFriendState(state, userId).pendingIncomingFriendIds
  ),
  userPendingIncomingFriends: (state, userId) => (
    selectors.userPendingIncomingFriendIds(state, userId).map(fid => {
      return userSelectors.find(state, fid)
    })
  ),
  userPendingOutgoingFriendIds: (state, userId) => (
    selectors.userFriendState(state, userId).pendingOutgoingFriendIds
  ),
  userPendingOutgoingFriends: (state, userId) => (
    selectors.userPendingOutgoingFriendIds(state, userId).map(fid => (
      userSelectors.find(state, fid)
    ))
  )
}

export const FriendsState = Record({
  'friendIds': Set(),
  'pendingIncomingFriendIds': Set(),
  'pendingOutgoingFriendIds': Set()
})

// top-level state is a Map (userId -> FriendsState)
// so that we can track relationships for multiple users in the system.
const reducer = (state = Map(), action = {}) => {
  const currentUserId = getCurrentUser().id
  const userIdFromMeta = action.meta && action.meta.id

  // enforce state shape
  if (!currentUserId && !userIdFromMeta) {
    return state
  }
  if (userIdFromMeta && !state.get(userIdFromMeta)) {
    state = state.withMutations(s => s.set(userIdFromMeta, FriendsState()))
  }
  if (currentUserId && !state.get(currentUserId)) {
    state = state.withMutations(s => s.set(currentUserId, FriendsState()))
  }

  switch (action.type) {
    case fulfilled(userActions.friends.type): {
      let userId = userIdFromMeta

      const friendIds = state.getIn([userId, 'friendIds'])
        .merge(Set(_.map(action.payload.results, u => u.id)))

      return state.setIn([userId, 'friendIds'], friendIds)
    }
    case fulfilled(userActions.friendRequests.type): {
      let userId = userIdFromMeta

      const incomingIds = state.getIn([userId, 'pendingIncomingFriendIds'])
        .merge(Set(action.payload.meta.incoming_ids))
      const outgoingIds = state.getIn([userId, 'pendingOutgoingFriendIds'])
        .merge(Set(action.payload.meta.outgoing_ids))

      return state.setIn([userId, 'pendingOutgoingFriendIds'], outgoingIds)
        .setIn([userId, 'pendingIncomingFriendIds'], incomingIds)
    }
    case fulfilled(userActions.confirmFriendRequest.type): {
      let userId = currentUserId
      let friendId = userIdFromMeta

      const userFIds = state.getIn([userId, 'friendIds']).add(friendId)
      const uPendingIncoming = state.getIn([userId, 'pendingIncomingFriendIds'])
        .filter(id => id !== friendId)
      const uPendingOutgoing = state.getIn([userId, 'pendingOutgoingFriendIds'])
        .filter(id => id !== friendId)

      const friendFIds = state.getIn([friendId, 'friendIds']).add(userId)
      const fPendingIncoming = state.getIn([friendId, 'pendingIncomingFriendIds'])
        .filter(id => id !== userId)
      const fPendingOutgoing = state.getIn([friendId, 'pendingOutgoingFriendIds'])
        .filter(id => id !== userId)

      return state.setIn([userId, 'friendIds'], userFIds)
        .setIn([userId, 'pendingIncomingFriendIds'], uPendingIncoming)
        .setIn([userId, 'pendingOutgoingFriendIds'], uPendingOutgoing)
        .setIn([friendId, 'friendIds'], friendFIds)
        .setIn([friendId, 'pendingIncomingFriendIds'], fPendingIncoming)
        .setIn([friendId, 'pendingOutgoingFriendIds'], fPendingOutgoing)
    }
    case fulfilled(userActions.declineFriendRequest.type): {
      let userId = currentUserId
      let friendId = userIdFromMeta

      const uPendingIncoming = state.getIn([userId, 'pendingIncomingFriendIds']).filter(id => id !== friendId)
      const uPendingOutgoing = state.getIn([userId, 'pendingOutgoingFriendIds']).filter(id => id !== friendId)

      const fPendingIncoming = state.getIn([friendId, 'pendingIncomingFriendIds']).filter(id => id !== userId)
      const fPendingOutgoing = state.getIn([friendId, 'pendingOutgoingFriendIds']).filter(id => id !== userId)

      return state.setIn([userId, 'pendingIncomingFriendIds'], uPendingIncoming)
        .setIn([userId, 'pendingOutgoingFriendIds'], uPendingOutgoing)
        .setIn([friendId, 'pendingIncomingFriendIds'], fPendingIncoming)
        .setIn([friendId, 'pendingOutgoingFriendIds'], fPendingOutgoing)
    }
    case fulfilled(userActions.befriend.type): {
      let userId = currentUserId
      let friendId = userIdFromMeta

      const uPendingFIds = state.getIn([userId, 'pendingOutgoingFriendIds']).add(friendId)
      const fPendingFIds = state.getIn([friendId, 'pendingIncomingFriendIds']).add(userId)

      return state.setIn([userId, 'pendingOutgoingFriendIds'], uPendingFIds)
        .setIn([friendId, 'pendingIncomingFriendIds'], fPendingFIds)
    }
    case fulfilled(userActions.unfriend.type): {
      let userId = currentUserId
      let friendId = userIdFromMeta

      const userFriends = state.getIn([userId, 'friendIds']).filter(id => id !== parseInt(friendId))
      const friendFriends = state.getIn([friendId, 'friendIds']).filter(id => id !== parseInt(userId))

      const uPendingFIds = state.getIn([userId, 'pendingOutgoingFriendIds']).filter(id => id !== parseInt(friendId))
      const fPendingFIds = state.getIn([friendId, 'pendingIncomingFriendIds']).filter(id => id !== parseInt(userId))

      return state.setIn([userId, 'friendIds'], userFriends)
        .setIn([friendId, 'friendIds'], friendFriends)
        .setIn([userId, 'pendingOutgoingFriendIds'], uPendingFIds)
        .setIn([friendId, 'pendingIncomingFriendIds'], fPendingFIds)
    }
    default: {
      return state
    }
  }
}

const actions = {}

export {
  reducer,
  actions as friendsActions,
  selectors as friendsSelectors
}
